import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  BookOpen, 
  User, 
  MapPin, 
  ArrowLeft, 
  Heart, 
  Clock, 
  Calendar,
  Hash,
  Shield,
  Star,
  Globe,
  Book,
  Building,
  Tag,
  Check,
  UserPlus,
  AlertTriangle
} from 'lucide-react';
import UserStatsCard from '../components/UserStatsCard';
import FavoriteButton from '../components/FavoriteButton';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useLoading } from '../contexts/LoadingContext';
import LoadingButton from '../components/LoadingButton';
import Breadcrumb from '../components/Breadcrumb';
import UpgradeModal from '../components/UpgradeModal';
import { isOverdue } from '../utils/dateUtils';

interface BookDetails {
  id: string;
  title: string;
  author: string;
  isbn: string;
  published_date: string;
  description: string;
  cover_image: string;
  is_available: boolean | number;
  owner_name: string;
  owner_city: string;
  owner_id: string;
  created_at: string;
  has_requested?: boolean;
  rental_info?: {
    start_date: string;
    end_date: string;
    duration_days: number;
    renter_name: string;
  };
  // Informations supplémentaires de Google Books
  google_id?: string;
  language?: string;
  categories?: string[];
  page_count?: number;
  publisher?: string;
  average_rating?: number;
  ratings_count?: number;
}

const BookDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError, showBook } = useToast();
  const { isLoading, setLoading: setLoadingState, withLoading } = useLoading();
  const [book, setBook] = useState<BookDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchBookDetails();
    }
  }, [id]);

  const fetchBookDetails = async () => {
    setLoading(true);
    
    try {
      const response = await axios.get(`/api/books/${id}`);
      // Normaliser is_available en booléen pour éviter l'affichage du "0"
      let bookData = {
        ...response.data,
        is_available: Boolean(response.data.is_available)
      };

      // Récupérer les détails supplémentaires depuis Google Books si google_id est disponible
      if (bookData.google_id) {
        const additionalDetails = await fetchAdditionalBookDetails(bookData.google_id);
        if (additionalDetails) {
          bookData = {
            ...bookData,
            ...additionalDetails
          };
        }
      }

      setBook(bookData);
    } catch (error: any) {
      showError('Erreur', 'Erreur lors du chargement des détails du livre');
      console.error('Fetch book details error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour formater le nom (prénom + initiale du nom)
  const formatOwnerName = (fullName: string) => {
    if (!fullName) return '';
    const nameParts = fullName.trim().split(' ');
    if (nameParts.length === 1) {
      return nameParts[0]; // Si un seul mot, on l'affiche tel quel
    }
    const firstName = nameParts[0];
    const lastNameInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastNameInitial}.`;
  };

  // Fonction pour récupérer les détails supplémentaires depuis Google Books API
  const fetchAdditionalBookDetails = async (googleId: string) => {
    try {
      const response = await axios.get(`/api/books/google-details/${googleId}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des détails Google Books:', error);
      return null;
    }
  };

  const requestBook = async () => {
    if (!book) return;
    
    const loadingKey = `request-book-details-${book.id}`;
    
    setLoadingState(loadingKey, true);
    try {
      await axios.post(`/api/users/request-book/${book.id}`);
      
      // Mettre à jour l'état local pour indiquer que la demande a été faite
      setBook(prev => prev ? { ...prev, has_requested: true } : null);
      
      showBook('Demande envoyée', 'Votre demande d\'emprunt a été envoyée avec succès');
    } catch (error: any) {
      if (error.response?.status === 403 && error.response?.data?.code === 'BORROW_LIMIT') {
        setUpgradeOpen(true);
      } else if (error.message === 'Request timeout') {
        showError('Erreur', 'La demande a pris trop de temps. Vérifiez vos demandes pour voir si elle a été envoyée.');
      } else {
        showError('Erreur', error.response?.data?.error || 'Erreur lors de la demande d\'emprunt');
      }
    } finally {
      setLoadingState(loadingKey, false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPublicationDate = (dateString: string) => {
    if (!dateString) return 'Date inconnue';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Date inconnue';
      }
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Date inconnue';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-stone-200 border-t-forest-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des détails du livre...</p>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Livre non trouvé
          </h2>
          <p className="text-gray-600 mb-6">
            Le livre que vous recherchez n'existe pas ou a été supprimé.
          </p>
          <button
            onClick={() => navigate('/library')}
            className="inline-flex items-center px-6 py-3 bg-forest-600 text-white font-semibold rounded-lg hover:bg-forest-700 transition-all duration-200"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour à la bibliothèque
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <Breadcrumb
        items={[
          { label: 'Bibliothèque', to: '/library' },
          { label: 'Détails du livre' }
        ]}
      />
      {/* Header épuré aligné au breadcrumb (fond transparent) */}
      <div className="bg-transparent">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/library')}
              className="inline-flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar - Image et actions */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-8">
              {/* Image du livre */}
              <div className="text-center mb-6">
                {book.cover_image ? (
                  <img
                    src={book.cover_image}
                    alt={book.title}
                    className="w-full max-w-xs mx-auto rounded-xl shadow-lg"
                  />
                ) : (
                  <div className="w-full max-w-xs mx-auto h-80 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center">
                    <BookOpen className="h-20 w-20 text-gray-400" />
                  </div>
                )}
              </div>
              
              {/* Statut */}
              <div className="text-center mb-6">
                <div className="flex flex-col items-center space-y-2">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                    book.is_available
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {book.is_available ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Disponible
                      </>
                    ) : (
                      <>
                        <Clock className="h-4 w-4 mr-2" />
                        Emprunté
                      </>
                    )}
                  </span>
                  
                  {/* Badge "En retard" */}
                  {!book.is_available && book.rental_info && isOverdue(book.rental_info.end_date) && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 border border-orange-200">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      En retard
                    </span>
                  )}
                </div>
              </div>

              {/* Bouton favori */}
              {book.owner_id !== user?.id && (
                <div className="flex justify-center mb-4">
                  <FavoriteButton 
                    bookId={book.id} 
                    size="lg" 
                    showCount={false}
                  />
                </div>
              )}

              {/* Action principale */}
              {book.is_available && book.owner_id !== user?.id && (
                <div className="space-y-4">
                  {book.has_requested ? (
                    <div className="text-center p-4 bg-forest-50 rounded-xl border border-forest-200">
                      <div className="inline-flex items-center px-4 py-2 bg-forest-100 text-forest-800 rounded-lg mb-3">
                        <Clock className="h-4 w-4 mr-2" />
                        Demande en cours
                      </div>
                      <p className="text-sm text-gray-600">
                        Votre demande a été envoyée au propriétaire.
                      </p>
                    </div>
                  ) : (
                    <LoadingButton
                      onClick={requestBook}
                      loading={isLoading(`request-book-details-${book.id}`)}
                      loadingText="Envoi en cours..."
                      className="w-full"
                      variant="primary"
                      size="lg"
                    >
                      <UserPlus className="h-5 w-5 mr-2" />
                      Demander à emprunter
                    </LoadingButton>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Contenu principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informations principales */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <div className="mb-8">
                <h1 className="font-serif text-3xl text-ink mb-3 leading-tight">
                  {book.title}
                </h1>
                <p className="text-xl text-gray-600">
                  par <span className="font-semibold text-gray-900">{book.author}</span>
                </p>
              </div>

              {/* Informations détaillées */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {book.isbn && (
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Hash className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">ISBN</p>
                      <p className="text-gray-900 font-mono">{book.isbn}</p>
                    </div>
                  </div>
                )}
                
                {book.published_date && (
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Publication</p>
                      <p className="text-gray-900">{formatPublicationDate(book.published_date)}</p>
                    </div>
                  </div>
                )}
                
                {book.page_count && (
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Book className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pages</p>
                      <p className="text-gray-900">{book.page_count}</p>
                    </div>
                  </div>
                )}

                {book.language && book.language !== 'unknown' && (
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Globe className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Langue</p>
                      <p className="text-gray-900 capitalize">{book.language}</p>
                    </div>
                  </div>
                )}

                {book.publisher && (
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Building className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Éditeur</p>
                      <p className="text-gray-900">{book.publisher}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Ajouté le</p>
                    <p className="text-gray-900">{formatDate(book.created_at)}</p>
                  </div>
                </div>
              </div>

              {/* Catégories */}
              {book.categories && book.categories.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center">
                    <Tag className="h-4 w-4 mr-2" />
                    Catégories
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {book.categories.slice(0, 6).map((category: string, index: number) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                      >
                        {category}
                      </span>
                    ))}
                    {book.categories.length > 6 && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        +{book.categories.length - 6} autres
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Propriétaire */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <User className="h-5 w-5 mr-3 text-forest-700" />
                Propriétaire
              </h2>
              
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-12 h-12 bg-forest-100 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-forest-700" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {formatOwnerName(book.owner_name)}
                  </h3>
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-4 w-4 mr-2" />
                    <span>{book.owner_city}</span>
                  </div>
                </div>
              </div>
              
              <UserStatsCard 
                userId={book.owner_id} 
                userName={formatOwnerName(book.owner_name)}
                compact={true}
                hideTrustScore={false}
              />
            </div>

            {/* Description */}
            {book.description && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <BookOpen className="h-5 w-5 mr-3 text-forest-700" />
                  Description
                </h2>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    {book.description}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
};

export default BookDetailsPage; 